// @ts-nocheck
import React from 'react';
import type { AfterModifier, Modifier } from 'react-day-picker';

import * as styles from './styles';
import { cn } from '@cobliteam/ui/utils';
import { RadioGroup } from '@material-ui/core';
import moment from 'moment';

import type { DateRange } from '@cobliteam/common-types';

import { MultiDatePicker, RadioButton, SingleDatePicker } from '@cobliteam/base-components';
import type { IntervalChip } from '@cobliteam/base-components/src/DatePicker/MultiDatePicker';

import { CaptionError } from '../typography';

export enum PeriodSelectionOption {
  DAY = 'day',
  PERIOD = 'period',
}

export interface PeriodSelectionProps {
  period?: DateRange;
  periodLimitInDays?: number;
  initialDateOption?: PeriodSelectionOption;
  intervalChips?: IntervalChip[];
  hasNoDisabledDays?: boolean;
  modifier?: Modifier;
  className?: string;
  pickerWrapperClassName?: string;
  disabled?: boolean;
  onPeriodChange: (period: DateRange) => void;
  onPeriodInvalid?: () => void;
  dataTest?: string;
  defaultPeriodDays?: number;
  limitLastDay?: boolean;
  showDateOptions?: boolean;
}

export const defaultIntervalChips: IntervalChip[] = [
  {
    label: 'Últimos 7 dias',
    startDate: moment().startOf('day').subtract(7, 'days').valueOf(),
    endDate: moment().endOf('day').valueOf(),
  },
  {
    label: 'Últimos 30 dias',
    startDate: moment().startOf('day').subtract(30, 'days').valueOf(),
    endDate: moment().endOf('day').valueOf(),
  },
];

/**
 * @deprecated This component is deprecated and will be removed in the future.
 * @description Now use '@cobliteam/ui/components/*' components.
 */
export const PeriodSelection: React.FC<PeriodSelectionProps> = ({
  period,
  periodLimitInDays = 31,
  initialDateOption,
  intervalChips = defaultIntervalChips,
  hasNoDisabledDays,
  modifier = hasNoDisabledDays ? null : { after: new Date() },
  className,
  pickerWrapperClassName,
  disabled,
  onPeriodChange,
  onPeriodInvalid,
  dataTest,
  defaultPeriodDays = 7,
  limitLastDay = true,
  showDateOptions = true,
}) => {
  const [dateOption, setDateOption] = React.useState(initialDateOption || PeriodSelectionOption.PERIOD);
  const [isPeriodInvalid, setIsPeriodInvalid] = React.useState(false);

  const handleRadioDateChange = (event: React.ChangeEvent<{}>, selectedDate: string) => {
    const selectedDateOption = selectedDate as PeriodSelectionOption;

    const afterLimit = modifier ? (modifier as AfterModifier).after : undefined;
    const adjustedEndDate = afterLimit ? moment(afterLimit).endOf('day') : moment().endOf('day');
    const adjustedEndMillis = adjustedEndDate.valueOf();

    const newPeriod =
      selectedDateOption === PeriodSelectionOption.DAY
        ? {
            start: adjustedEndDate.startOf('day').valueOf(),
            end: adjustedEndMillis,
          }
        : {
            start: adjustedEndDate.subtract(defaultPeriodDays, 'day').startOf('day').valueOf(),
            end: adjustedEndMillis,
          };

    setDateOption(selectedDateOption);
    setIsPeriodInvalid(false);

    if (period) {
      onPeriodChange(newPeriod);
    }
  };

  const handleDayChange = (beginDayInMillis: number) => {
    const newPeriodEndMoment = moment(beginDayInMillis).endOf('day');

    const afterLimit = modifier ? (modifier as AfterModifier).after : undefined;
    const adjustedEndDate = afterLimit ? moment(afterLimit).endOf('day') : newPeriodEndMoment;

    if (newPeriodEndMoment > adjustedEndDate) {
      setIsPeriodInvalid(true);
    } else {
      const newPeriod = {
        start: beginDayInMillis,
        end: newPeriodEndMoment.valueOf(),
      };
      setIsPeriodInvalid(false);
      onPeriodChange(newPeriod);
    }
  };

  const handleRangeChange = (startInMilli: number, endInMilli: number) => {
    const newEndMillis = moment(endInMilli).endOf('day').valueOf();
    const daysRange = moment(newEndMillis).diff(moment(startInMilli), 'days');
    const daysRangeComparator = limitLastDay ? daysRange >= periodLimitInDays : daysRange > periodLimitInDays;
    if (daysRangeComparator) {
      setIsPeriodInvalid(true);
      if (onPeriodInvalid) {
        onPeriodInvalid();
      }
    } else {
      const newPeriod = { start: startInMilli, end: newEndMillis };
      setIsPeriodInvalid(false);
      onPeriodChange(newPeriod);
    }
  };

  return (
    <div className={cn(styles.periodContainer, className)}>
      <div className={cn(styles.pickerWrapper, pickerWrapperClassName)}>
        {showDateOptions && (
          <RadioGroup value={dateOption} onChange={handleRadioDateChange} className={styles.radioGroup}>
            <RadioButton value={PeriodSelectionOption.PERIOD} disabled={disabled} label='Período' />
            <RadioButton value={PeriodSelectionOption.DAY} disabled={disabled} label='Dia único' />
          </RadioGroup>
        )}
        {dateOption === PeriodSelectionOption.DAY && (
          <div className={styles.pickerDate}>
            <SingleDatePicker
              placeholder='DD/MM/AAAA'
              handleDayChange={handleDayChange}
              selectedDateInMillis={period?.start}
              error={isPeriodInvalid}
              disabledDays={modifier}
              disabled={disabled}
            />
          </div>
        )}
        {dateOption === PeriodSelectionOption.PERIOD && (
          <div className={styles.pickerPeriod}>
            <MultiDatePicker
              handleRangeChange={handleRangeChange}
              startDate={period?.start}
              endDate={period?.end}
              showWarningError={isPeriodInvalid}
              showErrorMessage={false}
              disabledDays={modifier}
              intervalChips={intervalChips}
              dataTest={dataTest}
            />
            <div className={styles.errorWrapper}>
              {isPeriodInvalid && (
                <CaptionError>
                  {'Período maior que '}
                  {periodLimitInDays}
                  {' dias'}
                </CaptionError>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
